import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  COUNTRY_EXPERIENCE_REGISTRY,
  COUNTRY_HOME_PATH,
  SUPPORTED_COUNTRIES,
  getCountryExperience,
  normalizeCountry,
  type CountryCode,
  type CountryExperienceConfig,
} from "./countryExperienceEngine";
import { trackEvent } from "@/lib/audit";

const STORAGE_KEY = "vs:country_preference_v1";
const DEFAULT_COUNTRY: CountryCode = "BR";

interface CountryContextValue {
  country: CountryCode;
  experience: CountryExperienceConfig;
  setCountry: (code: CountryCode, opts?: { source?: "manual" | "geo" | "url" }) => void;
  supported: CountryCode[];
}

const Ctx = createContext<CountryContextValue | null>(null);

function readStoredCountry(): CountryCode | null {
  if (typeof window === "undefined") return null;
  try {
    return normalizeCountry(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

export function CountryProvider({ children }: { children: ReactNode }) {
  const [country, setCountryState] = useState<CountryCode>(
    () => readStoredCountry() ?? DEFAULT_COUNTRY,
  );

  // Lazy geo detection — only when no manual preference stored.
  useEffect(() => {
    if (readStoredCountry()) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/public/geoip", { cache: "no-store" });
        const json = (await res.json()) as { country?: string | null };
        const c = normalizeCountry(json?.country ?? null);
        if (!cancelled && c) {
          setCountryState(c);
          void trackEvent("country_switched", { code: c, source: "geo" });
        }
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const setCountry = useCallback(
    (code: CountryCode, opts?: { source?: "manual" | "geo" | "url" }) => {
      setCountryState(code);
      try { window.localStorage.setItem(STORAGE_KEY, code); } catch { /* noop */ }
      void trackEvent("country_switched", { code, source: opts?.source ?? "manual" });
    },
    [],
  );

  const value = useMemo<CountryContextValue>(
    () => ({
      country,
      experience: COUNTRY_EXPERIENCE_REGISTRY[country],
      setCountry,
      supported: SUPPORTED_COUNTRIES,
    }),
    [country, setCountry],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCountry(): CountryContextValue {
  const v = useContext(Ctx);
  if (v) return v;
  // Fallback when provider is absent (defensive): default to BR.
  return {
    country: DEFAULT_COUNTRY,
    experience: COUNTRY_EXPERIENCE_REGISTRY[DEFAULT_COUNTRY],
    setCountry: () => {},
    supported: SUPPORTED_COUNTRIES,
  };
}

export function useCountryExperience(code?: CountryCode | null) {
  const ctx = useCountry();
  if (!code) return ctx.experience;
  return getCountryExperience(code) ?? ctx.experience;
}

export { COUNTRY_HOME_PATH };